/***
 *
 *   PERIODIC REVIEW DETAIL
 *   Resolve a single periodic review (FR-013): pick exactly one of the four
 *   actions. Full re-KYC shows a reviewer-editable contact-confirmation step,
 *   pre-filled from a real-time source lookup where the customer came from an
 *   integration (FR-015/FR-016). Reads GET /api/periodic-review/:id and posts
 *   to /api/periodic-review/:id/resolve.
 *
 **********/

import React, { Fragment, useState, useEffect, useCallback, useContext } from "react";
import Axios from "axios";
import { useParams } from "react-router-dom";
import {
  Animate,
  Button,
  Card,
  Loader,
  Message,
  TitleRow,
  ViewContext,
} from "components/lib";

const ACTIONS = [
  { value: "re_kyc", label: "Full re-KYC (re-onboard the customer)" },
  { value: "aml_only", label: "AML screening only" },
  { value: "edd", label: "Open an EDD case" },
  { value: "close", label: "Close — no longer a customer" },
];

export function PeriodicReviewDetailView() {
  const params = useParams();
  const context = useContext(ViewContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [action, setAction] = useState("re_kyc");
  const [contact, setContact] = useState({ email: "", first_name: "", last_name: "" });
  const [reason, setReason] = useState("");
  const [lookupNote, setLookupNote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchCase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Axios.get(`/api/periodic-review/${params.id}`);
      const payload = response.data?.data || null;
      setData(payload);
      const v = payload?.verification || {};
      setContact({
        email: v.email || "",
        first_name: v.first_name || "",
        last_name: v.last_name || "",
      });
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to load this review right now."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [params.id]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const refreshContact = async () => {
    setLookupNote("Looking up the latest contact details...");
    try {
      const response = await Axios.get(
        `/api/periodic-review/${params.id}/contact-lookup`
      );
      const result = response.data?.data || {};
      setContact({
        email: result.email || "",
        first_name: result.first_name || "",
        last_name: result.last_name || "",
      });
      setLookupNote(
        result.source === "integration"
          ? "Pre-filled from the source integration — edit if needed before sending."
          : result.lookup_error ||
              "Using stored details — edit if needed before sending."
      );
    } catch (err) {
      setLookupNote("Could not reach the source; using the details on file.");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { action };
      if (action === "re_kyc") {
        payload.email = contact.email;
        payload.first_name = contact.first_name;
        payload.last_name = contact.last_name;
      }
      if (action === "close") payload.reason = reason;

      await Axios.post(`/api/periodic-review/${params.id}/resolve`, payload);
      context.notification.show("Review resolved", "success", true, "toast", "check");
      window.location = "/account/periodic-reviews";
    } catch (err) {
      context.handleError(err);
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <Message type="error" title="Periodic review" text={error} />;

  const reviewCase = data?.case || {};
  const verification = data?.verification || {};
  const contactSource = data?.contact_source || {};
  const history = Array.isArray(data?.history) ? data.history : [];
  const name = [verification.first_name, verification.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <Fragment>
      <Animate>
        <TitleRow title="Resolve periodic review" />

        <Card title="Customer">
          <p>
            <strong>{name || verification.email || "Unknown customer"}</strong>
          </p>
          <p className="text-sm text-gray-500">{verification.email}</p>
          <p className="text-sm text-gray-500">
            Rating: {reviewCase.rating || "-"} &middot; Due:{" "}
            {reviewCase.due_date || "-"} &middot; Status: {reviewCase.status || "-"}
          </p>
        </Card>

        <Card title="Choose an action">
          {ACTIONS.map((a) => (
            <label key={a.value} className="block mb-2">
              <input
                type="radio"
                name="action"
                value={a.value}
                checked={action === a.value}
                onChange={() => setAction(a.value)}
                className="mr-2"
              />
              {a.label}
            </label>
          ))}

          {action === "re_kyc" && (
            <div className="mt-4">
              <Message
                type="info"
                title="Confirm the customer's contact details"
                text="A new Tranche 2 verification link will be sent to this address. Confirm or edit the details before sending."
              />
              {contactSource.integration && (
                <Button
                  small
                  text="Refresh from source"
                  action={refreshContact}
                />
              )}
              {lookupNote && (
                <p className="text-sm text-gray-500 mt-2">{lookupNote}</p>
              )}
              <label className="block mt-3">
                First name
                <input
                  type="text"
                  value={contact.first_name}
                  onChange={(e) =>
                    setContact({ ...contact, first_name: e.target.value })
                  }
                  className="block w-full border rounded p-2 mt-1"
                />
              </label>
              <label className="block mt-3">
                Last name
                <input
                  type="text"
                  value={contact.last_name}
                  onChange={(e) =>
                    setContact({ ...contact, last_name: e.target.value })
                  }
                  className="block w-full border rounded p-2 mt-1"
                />
              </label>
              <label className="block mt-3">
                Email
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    setContact({ ...contact, email: e.target.value })
                  }
                  className="block w-full border rounded p-2 mt-1"
                />
              </label>
            </div>
          )}

          {action === "edd" && (
            <Message
              type="info"
              title="Open an EDD case"
              text="Only EDD-capable assessors can open and complete an EDD case. The review stays open until the case is closed."
            />
          )}

          {action === "aml_only" && (
            <Message
              type="info"
              title="AML screening only"
              text="Runs a PEP/Sanctions and adverse-media screen and advances the next review date from the current rating."
            />
          )}

          {action === "close" && (
            <label className="block mt-3">
              Reason for closing
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="block w-full border rounded p-2 mt-1"
                rows={3}
              />
            </label>
          )}

          <div className="mt-4">
            <Button
              text={submitting ? "Working..." : "Resolve review"}
              action={submit}
            />
          </div>
        </Card>

        {history.length > 0 && (
          <Card title="Review history">
            {history.map((h) => (
              <p key={h.id} className="text-sm text-gray-600 mb-1">
                {h.created_at} &middot; {h.event_type} &middot;{" "}
                {h.selected_action || h.status} {h.rating ? `(${h.rating})` : ""}
              </p>
            ))}
          </Card>
        )}
      </Animate>
    </Fragment>
  );
}
